import { AxiosResponse, AxiosError } from "axios";
import api from "@services/axios/apiManager";

/** 卡片歸屬：個人卡（綁在自己名下）vs 帳戶卡（公司卡，屬於 context）。 */
export type CardScope = "personal" | "context";

/**
 * 綁卡完成後的導回意圖（3DS challenge 用；§6）：
 *   - pay_order：從付款頁綁新卡 → challenge 完成後導回 return_path 繼續付款。
 *   - wallet：從個人錢包 / 帳戶付款方式頁綁卡 → challenge 完成後回卡片列表。
 */
export type ReturnContext = { type: "pay_order"; order_id: string; return_path: string } | { type: "wallet" };

export interface RegisterPaymentMethodParams {
  method_type: "credit_card";
  prime: string;
  nickname: string;
  /** 預設 personal。`context` = 為此帳戶綁一張公司卡（需 owner 權限 + 顯式 X-Context）。 */
  scope?: CardScope;
  /** personal 卡是否同時授權給當前 context 的成員（「從個人錢包加入帳戶」）。 */
  share_with_context?: boolean;
  /** 3DS challenge 導回用；不帶則後端視為無 return_context（落地頁無法自動導回）。 */
  return_context?: ReturnContext;
}

/** POST /customer/payment-methods 成功回應（§6 discriminated union，判別欄位為 `bind_status`）。 */
export type BindPaymentMethodResult =
  | {
      bind_status: "bound";
      payment_method: OwnedPaymentMethod;
      payment_url: null;
      bind_ref: null;
    }
  | {
      bind_status: "challenge_required";
      payment_method: null;
      payment_url: string;
      bind_ref: string;
    };

/** GET /app-api/customer/payment-methods 回傳的單筆已綁付款方式 */
export interface OwnedPaymentMethod {
  payment_method_id: string;
  method_type: string;
  status: string;
  nickname: string;
  card?: {
    bincode?: string;
    lastfour?: string;
    issuer?: string;
    expiry_month?: number;
    expiry_year?: number;
  };
  /** 扣款推播通知偏好（每次扣款是否推播給綁卡者） */
  notify_on_charge?: boolean;
  /** 詳情 API 才有：此卡授權可用的帳戶（= 已啟用帳戶） */
  authorizations?: { context_id: number; authorized_customer_id: number }[];
  /** 詳情 API 才有：caller 在哪些帳戶以此卡為預設付款 */
  default_context_ids?: number[];
  create_time_stamp: string;
}

/** GET /app-api/payments/order/:platform_order_id — 付款頁開頁查詢（caller 必為訂單 payer） */
export interface OrderPaymentStatus {
  platform_order_id: string;
  /** 訂單付款人 context_id；付款頁據此撈可選卡（不依賴 app 當下選定身分，FM 深連結必需） */
  context_id: number | null;
  order_status: string;
  /** 線上付款狀態：none / awaiting_payment / paid / cash_fallback（配送員已改收現金） */
  payment_status?: string;
  amount: number | null;
  payment_method: string | null;
  label: string | null;
  payment_method_id: string | null;
  paid: boolean;
  payable: boolean;
  transaction: {
    payment_transaction_id: string;
    status: string;
    amount: number;
    paid_at: string | null;
    lastfour: string | null;
    issuer: string | null;
  } | null;
  /**
   * 客戶付款 PIN 碼（4 位數字，電子付款的在場憑證）：配送員到場收款時由客戶唸給配送員核對。
   * 只有 GET payments/order/:id 帶；已付／改收現金／已結案為 null；pay-link/status 不帶。
   */
  payment_pin?: string | null;
}

/** 供應商快照品項（僅取顯示需要的欄位） */
export interface SnapshotItem {
  kind: "gas" | "commodity";
  gas_type?: string | null;
  gas_kilogram?: number | string | null;
  commodity_name?: string | null;
  numbers_of_cylinder?: number;
  numbers_of_commodity?: number;
}

export const handleAPIGetOrderPaymentStatus = async (platformOrderId: string, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/payments/order/${platformOrderId}`, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const, data: res.data?.data as OrderPaymentStatus };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/**
 * POST /app-api/payments/intents — 以已綁卡對訂單扣款（同步；金額由後端從訂單反查，防竄改）。
 * 冪等：X-Client-Request-Id；同一鍵不會重複扣款。
 */
export const handleAPICreatePaymentIntent = async (platformOrderId: string, paymentMethodId: string, cusJwtToken: string) => {
  const idem = crypto.randomUUID ? crypto.randomUUID() : "pay-" + Date.now();
  try {
    const res: AxiosResponse = await api.post(
      "/app-api/payments/intents",
      { platform_order_id: platformOrderId, customer_payment_method_id: paymentMethodId },
      { headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Client-Request-Id": idem } },
    );
    return { state: "success" as const, data: res.data?.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** 一次性付款連結（pay-link）頁面顯示用摘要（供應商 / 訂單編號 / 品項 / 完成後導回網址）。 */
export interface PayLinkSummary {
  supplier_name: string;
  display_id: string;
  items: SnapshotItem[];
  return_url: string | null;
}

/** GET /app-api/payments/pay-link/status 回應：訂單付款狀態 + pay-link 頁面摘要。 */
export type PayLinkStatus = OrderPaymentStatus & { pay_link: PayLinkSummary };

/**
 * POST /app-api/payments/order/:platform_order_id/pay-token — 簽發一次性付款連結的 pay_token（Apple Pay 掃碼／外開 Safari 用）。
 * 需 LINE 登入的 Bearer JWT；後端驗 caller 為訂單 payer。
 */
export const handleAPIIssuePayToken = async (platformOrderId: string, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.post(
      `/app-api/payments/order/${platformOrderId}/pay-token`,
      {},
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success" as const, data: res.data?.data as { pay_token: string; expires_at: string; url: string } };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/**
 * GET /app-api/payments/pay-link/status — 一次性付款連結（Safari 模式）開頁查詢，帶 pay_token 而非 LINE 登入。
 * 失敗時額外回 `status`，供頁面判斷 401（PAY_TOKEN_INVALID / PAY_TOKEN_USED / PAY_TOKEN_EXPIRED）。
 */
export const handleAPIGetPayLinkStatus = async (payToken: string) => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/payments/pay-link/status`, {
      headers: { "X-Pay-Token": payToken },
    });
    return { state: "success" as const, data: res.data?.data as PayLinkStatus };
  } catch (error) {
    const axiosError = error as AxiosError;
    return {
      state: "error" as const,
      error: (axiosError.response?.data as any)?.message || axiosError.message,
      status: axiosError.response?.status,
    };
  }
};

/**
 * POST /app-api/payments/pay-link/intents — 一次性付款連結模式下以 Apple Pay 扣款（帶 pay_token，不帶 Authorization）。
 * 冪等：X-Client-Request-Id；同一鍵不會重複扣款。
 */
export const handleAPICreatePayLinkIntent = async (payToken: string, platformOrderId: string, prime: string) => {
  const idem = crypto.randomUUID ? crypto.randomUUID() : "pay-" + Date.now();
  try {
    const res: AxiosResponse = await api.post(
      `/app-api/payments/pay-link/intents`,
      { platform_order_id: platformOrderId, method_type: "apple_pay", prime },
      { headers: { "X-Pay-Token": payToken, "X-Client-Request-Id": idem } },
    );
    return { state: "success" as const, data: res.data?.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/**
 * POST /app-api/customer/payment-methods/:id/authorizations — 授權某帳戶成員使用此卡。
 * 不帶 authorizedCustomerId 時為 self-authorize（把自己的既有個人卡加進帳戶）。
 */
export const handleAPIGrantAuthorization = async (
  paymentMethodId: string,
  contextId: string | number,
  cusJwtToken: string,
  authorizedCustomerId?: number,
) => {
  try {
    await api.post(
      `/app-api/customer/payment-methods/${paymentMethodId}/authorizations`,
      { context_id: Number(contextId), ...(authorizedCustomerId ? { authorized_customer_id: authorizedCustomerId } : {}) },
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success" as const };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** DELETE /app-api/customer/payment-methods/:id — 移除付款方式（軟刪除；歷史扣款紀錄保留） */
export const handleAPIUnbindPaymentMethod = async (paymentMethodId: string, cusJwtToken: string) => {
  try {
    await api.delete(`/app-api/customer/payment-methods/${paymentMethodId}`, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** PATCH /app-api/customer/payment-methods/:id — 更新暱稱 / 扣款推播偏好 */
export const handleAPIUpdatePaymentMethod = async (
  paymentMethodId: string,
  body: { nickname?: string | null; notify_on_charge?: boolean },
  cusJwtToken: string,
) => {
  try {
    const res: AxiosResponse = await api.patch(`/app-api/customer/payment-methods/${paymentMethodId}`, body, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const, data: res.data?.data as OwnedPaymentMethod };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** 列出 caller 已綁定的付款方式（信用卡 / LINE Pay…）。 */
export const handleAPIListPaymentMethods = async (cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.get("/app-api/customer/payment-methods", {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const, items: (res.data?.data?.items ?? []) as OwnedPaymentMethod[] };
  } catch (error) {
    const axiosError = error as AxiosError;
    return {
      state: "error" as const,
      error: (axiosError.response?.data as any)?.message || axiosError.message,
    };
  }
};

/** 單卡詳情 GET /app-api/customer/payment-methods/:id */
export const handleAPIGetPaymentMethodDetail = async (paymentMethodId: string, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/customer/payment-methods/${paymentMethodId}`, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const, data: res.data?.data as OwnedPaymentMethod };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** 單卡付費紀錄（spec API3）GET /app-api/customer/payment-methods/:id/payments */
export interface CardPaymentItem {
  payment_transaction_id: string;
  platform_order_id: string;
  /** 人類可讀訂單編號（= order_list.order_id，例 "ADO_8805"，後端取自 platform_order_supplier_link.order_id）；尚未綁定 supplier / order_id 未產生時為 null */
  display_id: string | null;
  amount: number;
  status: string;
  payment_method: string;
  provider: string;
  card_label: string | null;
  paid_at: string | null;
  created_at: string;
}

export const handleAPIListCardPayments = async (paymentMethodId: string, cusJwtToken: string, page = 1, limit = 10) => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/customer/payment-methods/${paymentMethodId}/payments`, {
      params: { page, limit },
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return {
      state: "success" as const,
      items: (res.data?.data?.items ?? []) as CardPaymentItem[],
      total: (res.data?.data?.total ?? 0) as number,
    };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

export const handleAPIRegisterPaymentMethod = async (
  params: RegisterPaymentMethodParams,
  cusJwtToken: string,
  /** scope=context 或 share_with_context=true 時必帶（`context:<id>`）。 */
  xContext?: string,
) => {
  const idem = crypto.randomUUID ? crypto.randomUUID() : "test-" + Date.now();
  try {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${cusJwtToken}`,
      "X-Client-Request-Id": idem,
    };
    if (xContext) headers["X-Context"] = xContext;
    const res: AxiosResponse = await api.post("/app-api/customer/payment-methods", params, { headers });
    return { state: "success" as const, data: res.data?.data as BindPaymentMethodResult };
  } catch (error) {
    const axiosError = error as AxiosError;
    return {
      state: "error" as const,
      error: (axiosError.response?.data as any)?.message || axiosError.message,
    };
  }
};

/** GET /app-api/customer/payment-methods/bind-result?bind_ref=… 回應（§6 落地頁輪詢用）。 */
export interface BindResult {
  status: "pending" | "succeeded" | "failed" | "expired";
  payment_method_id?: string;
  fail_reason?: string;
  return_context: ReturnContext | null;
}

/** 3DS 落地頁輪詢：查詢 bind_ref 對應的綁卡結果。 */
export const handleAPIBindResult = async (bindRef: string, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.get("/app-api/customer/payment-methods/bind-result", {
      params: { bind_ref: bindRef },
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return { state: "success" as const, data: res.data?.data as BindResult };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** GET /app-api/customer/contexts/:id/payment-methods 的單筆（帳戶可用清單）。 */
export interface ContextPaymentMethod {
  payment_method_id: string;
  card_scope: CardScope;
  method_type: string;
  status: string;
  nickname: string;
  card?: {
    bincode?: string;
    lastfour?: string;
    issuer?: string;
    expiry_month?: number;
    expiry_year?: number;
  };
  owner_customer_id: number | null;
  is_owned_by_caller: boolean;
  is_default: boolean;
  /** 把這張卡加進來的人的顯示名稱（「由誰加入」標示用） */
  added_by_name?: string | null;
  create_time_stamp: string;
}

/** 列出 caller 在某 context 內可用的所有付款方式（帳戶卡 + 自有 + 被授權）。 */
export const handleAPIListContextMethods = async (contextId: string | number, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.get(`/app-api/customer/contexts/${contextId}/payment-methods`, {
      headers: { Authorization: `Bearer ${cusJwtToken}` },
    });
    return {
      state: "success" as const,
      items: (res.data?.data?.items ?? []) as ContextPaymentMethod[],
      defaultId: (res.data?.data?.default_payment_method_id ?? null) as string | null,
    };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};

/** 設定 caller 在此 context 的預設付款方式（null = 清除）。 */
export const handleAPISetContextDefault = async (contextId: string | number, paymentMethodId: string | null, cusJwtToken: string) => {
  try {
    const res: AxiosResponse = await api.patch(
      `/app-api/customer/contexts/${contextId}/payment-default`,
      { payment_method_id: paymentMethodId },
      { headers: { Authorization: `Bearer ${cusJwtToken}` } },
    );
    return { state: "success" as const, data: res.data?.data };
  } catch (error) {
    const axiosError = error as AxiosError;
    return { state: "error" as const, error: (axiosError.response?.data as any)?.message || axiosError.message };
  }
};
